import { getSupabaseClient } from "@/online/supabaseClient";
import { AuthService } from "@/online/AuthService";
import { OnlineFeaturesStorage } from "@/online/OnlineFeaturesStorage";
import { callFunction } from "@/online/functionsClient";
import type { ServiceResult } from "@/online/types";
import type { Platform } from "@/platform/Platform";
import { EndlessQueue, type EndlessFlushAttempt } from "./EndlessQueue";
import { ENDLESS_RULES_VERSION, type EndlessEndReason, type EndlessLeaderboardEntry, type EndlessMyRank, type EndlessSubmissionMetrics } from "./types";

/**
 * What happened to one `submitResult()` call - never a thrown error.
 * `"rejected"` (a definitive, permanently-invalid 4xx - never retried) is
 * distinguished from `"queued"` (a transient failure, still queued to
 * retry later) so callers/analytics can tell "this run's data was invalid"
 * apart from "this run will be retried" - both leave `EndlessQueue` in the
 * correct state either way (dropped vs. still queued), this is purely a
 * richer signal for the caller.
 */
export type EndlessSubmitOutcome = "sent" | "queued" | "rejected" | "disabled";

/**
 * `submitResult()`'s full return value - richer than Campaign's plain
 * `SubmitRunOutcome` on purpose: Endless analytics (`endless_new_online_best`)
 * needs to know whether THIS specific submission actually improved the
 * caller's stored online best, which only the server's own response can
 * say. `isNewBest`/`rank` are only meaningful when `outcome === "sent"` -
 * `undefined` otherwise (queued/disabled never got a server response yet).
 */
export interface EndlessSubmitResult {
  outcome: EndlessSubmitOutcome;
  isNewBest?: boolean;
  rank?: number | null;
}

interface EndlessFlushResult extends EndlessFlushAttempt {
  isNewBest?: boolean;
  rank?: number | null;
}

interface EndlessLeaderboardRow {
  rank: number;
  display_name: string;
  length: number;
  score: number;
  survival_time_ms: number;
  end_reason: EndlessEndReason;
}

interface EndlessMyRankRow {
  rank: number | null;
  length: number;
  score: number;
  survival_time_ms: number;
  end_reason: EndlessEndReason | null;
  has_result: boolean;
}

interface EndlessResultResponse {
  accepted: boolean;
  isNewBest: boolean;
  rank: number | null;
}

/**
 * The Endless-specific leaderboard service - a narrow, self-contained
 * counterpart to Campaign's `LeaderboardService`, never a swap-by-opt-in
 * factory like `getLeaderboardService()`. That distinction is deliberate:
 * unlike Campaign (where every leaderboard read is gated behind Online
 * Features), the Endless leaderboard's public read RPCs are viewable by
 * ANYONE, with no opt-in or signed-in identity required (see
 * `isLeaderboardAvailable()`) - only submitting a result or asking "what's
 * my rank" requires the Online Features opt-in (see
 * `isSubmissionAvailable()`), the same app-wide flag Campaign already uses.
 *
 * Reads call the public `get_endless_leaderboard`/`get_my_endless_rank`
 * RPCs directly via the browser Supabase client (anon/authenticated key),
 * same pattern as `SupabaseLeaderboardService`. Writes go through the
 * `endless-result` Netlify Function (never a direct table write - RLS
 * blocks that regardless). Every method resolves, never throws.
 */
class EndlessLeaderboardServiceImpl {
  /** Cheap, synchronous, no network. Viewing never requires Online Features or a submitted identity - only a configured Supabase client. */
  isLeaderboardAvailable(): boolean {
    return getSupabaseClient() !== null;
  }

  /** Cheap, synchronous, no network. Submitting a result or asking "what's my rank" requires the app-wide Online Features opt-in, same gate Campaign already uses. */
  isSubmissionAvailable(): boolean {
    return OnlineFeaturesStorage.isEnabled() && getSupabaseClient() !== null;
  }

  /** PR34: only `platform`'s board - legacy `unknown` rows are never mixed in. */
  async getLeaderboard(platform: Platform, rulesVersion: number = ENDLESS_RULES_VERSION): Promise<ServiceResult<EndlessLeaderboardEntry[]>> {
    if (!this.isLeaderboardAvailable()) return { state: "offline" };
    const client = getSupabaseClient();
    if (!client) return { state: "offline" };
    try {
      const { data, error } = await client.rpc("get_endless_leaderboard", { p_rules_version: rulesVersion, p_platform: platform });
      if (error) return this.classifyError();
      const rows = (data ?? []) as EndlessLeaderboardRow[];
      return {
        state: "ok",
        data: rows.map((row) => ({
          rank: row.rank,
          displayName: row.display_name,
          length: row.length,
          score: row.score,
          survivalTimeMs: row.survival_time_ms,
          endReason: row.end_reason,
        })),
      };
    } catch {
      return this.classifyError();
    }
  }

  /** PR34: the caller's rank on the same `platform` board `getLeaderboard()` shows. */
  async getMyRank(platform: Platform, rulesVersion: number = ENDLESS_RULES_VERSION): Promise<ServiceResult<EndlessMyRank>> {
    if (!this.isSubmissionAvailable()) return { state: "offline" };
    const client = getSupabaseClient();
    if (!client) return { state: "offline" };
    if (!(await AuthService.ensureSignedIn())) return this.classifyError();
    try {
      const { data, error } = await client.rpc("get_my_endless_rank", { p_rules_version: rulesVersion, p_platform: platform });
      if (error) return this.classifyError();
      const row = ((data ?? []) as EndlessMyRankRow[])[0];
      if (!row) return { state: "ok", data: { hasResult: false, rank: null, length: 0, score: 0, survivalTimeMs: 0, endReason: null } };
      return {
        state: "ok",
        data: {
          hasResult: row.has_result,
          rank: row.rank,
          length: row.length,
          score: row.score,
          survivalTimeMs: row.survival_time_ms,
          endReason: row.end_reason,
        },
      };
    } catch {
      return this.classifyError();
    }
  }

  /**
   * Local-first, by contract: the caller (`GameScene.finishEndlessRun()`)
   * must already have saved the local personal best (`EndlessStorage`)
   * before ever calling this - a submission failure here can never roll
   * that back, block `PLAY AGAIN`, or affect local play. Enqueues
   * synchronously, before any `await`, same "durable before yielding"
   * guarantee `SupabaseLeaderboardService.submitRun()` already relies on.
   * Every valid terminal run is submitted, not only a new local best - the
   * server's own monotonic upsert decides whether it actually improves the
   * stored online best.
   */
  /**
   * PR34: called by `GameScene.finishEndlessRun()` for every terminal run,
   * synchronously, before the result screen even opens. While Online
   * Features are off this keeps the run on this device only
   * (`EndlessQueue.captureWhileOffline()`, bounded to one best result) -
   * never sent, but no longer lost on PLAY AGAIN/refresh, so a later opt-in
   * can still submit it. While Online Features are on this is a no-op: the
   * result screen's normal `submitResult()` path enqueues and sends it.
   */
  captureResult(metrics: EndlessSubmissionMetrics): void {
    if (OnlineFeaturesStorage.isEnabled()) return;
    EndlessQueue.captureWhileOffline(metrics);
  }

  async submitResult(metrics: EndlessSubmissionMetrics): Promise<EndlessSubmitResult> {
    if (!this.isSubmissionAvailable()) return { outcome: "disabled" };
    EndlessQueue.enqueue(metrics);
    const outcomes = await this.flushQueueInternal();
    const own = outcomes.get(metrics.runId);
    if (own?.outcome === "sent") return { outcome: "sent", isNewBest: own.isNewBest, rank: own.rank };
    if (own?.outcome === "drop") return { outcome: "rejected" };
    return { outcome: "queued" };
  }

  /** Best-effort retry of anything already queued. Call at startup and on the browser's `online` event - same precedent as Campaign's `flushQueue()`. */
  async flushQueue(): Promise<void> {
    await this.flushQueueInternal();
  }

  private async flushQueueInternal(): Promise<Map<string, EndlessFlushResult>> {
    if (!this.isSubmissionAvailable()) return new Map();
    if (!(await AuthService.ensureSignedIn())) return new Map();

    return EndlessQueue.flush<EndlessFlushResult>(async (run) => {
      const token = await this.getAccessToken();
      if (!token) return { outcome: "retry-later" };
      const result = await callFunction<EndlessResultResponse>("endless-result", token, run);
      if (result.ok) return { outcome: "sent", isNewBest: result.data.isNewBest, rank: result.data.rank };
      // A 4xx means the server rejected this exact payload as invalid -
      // retrying an unchanged payload would only fail the same way again.
      if (result.status >= 400 && result.status < 500) return { outcome: "drop" };
      return { outcome: "retry-later" };
    });
  }

  /**
   * Same defensive wrap as `ScoreChallengeClient.ensureSignedIn()`/
   * `DailyChallengeClient.ensureSignedIn()`/
   * `SupabaseLeaderboardService.getAccessToken()`: an unexpected rejection
   * from `client.auth.getSession()` must resolve to "no token" (routing the
   * caller into its existing `"retry-later"` handling) rather than becoming
   * an unhandled rejection inside `EndlessQueue.flush()`'s submit callback,
   * which has no try/catch of its own around this call.
   */
  private async getAccessToken(): Promise<string | null> {
    try {
      const client = getSupabaseClient();
      if (!client) return null;
      const { data } = await client.auth.getSession();
      return data.session?.access_token ?? null;
    } catch {
      return null;
    }
  }

  private classifyError(): ServiceResult<never> {
    const knownOffline = typeof navigator !== "undefined" && navigator.onLine === false;
    return knownOffline ? { state: "offline" } : { state: "error", message: "Something went wrong. Please try again." };
  }
}

export const endlessLeaderboardService = new EndlessLeaderboardServiceImpl();
